import { useEffect, useState, useContext } from 'react';
import { useForm, Controller } from "react-hook-form";
import { TextField, MenuItem, Select, ListSubheader, FormControl, InputLabel, OutlinedInput, ToggleButtonGroup, ToggleButton, FormHelperText } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import apiAdmin from '../../services/api-admin';
import { AdminUserContext } from '../../contexts/user-context';

const CourtesySigningRequest = (props) => {
    const { adminUserProfile } = useContext(AdminUserContext);
    const { register, handleSubmit, control, reset, getValues, setValue, watch, clearErrors, setError, formState: { errors } } = useForm();
    const [offices, setOffices] = useState([]);
    const [regions, setRegions] = useState([]);
    const [selectedOffices, setSelectedOffices] = useState([]);
    const [pageError, setPageError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [timeZone, setTimeZone] = useState('Mountain Time');
    const [transactionData, setTransactionData] = useState(null);

    useEffect(() => {
        const getRegions = async () => {
            apiAdmin.get(`/regions-with-offices`)
                .then((response) => {
                    setRegions(response.data);
                    let officeArr = [];
                    response.data.forEach(region => {
                        const filteredOffices = region.offices.filter(o => o.courtesyEmail);
                        officeArr = officeArr.concat(filteredOffices.map(o => ({
                            id: o.officeId,
                            officeName: `${region.regionName}/${o.officeName}`,
                            courtesyEmail: o.courtesyEmail
                        })));
                    });
                    setOffices(officeArr);
                });
        };
        getRegions();
    }, []);

    const renderSelectGroup = (region) => {
        if (!region.offices.length) return null;

        const items = region.offices
            .filter(o => offices.some(office => office.id === o.officeId))
            .map(o => (
                <MenuItem key={'office_' + o.officeId} value={o.courtesyEmail}>
                    {o.officeName}
                </MenuItem>
            ));

        if (!items.length) return null;

        return [
            <ListSubheader key={`subheader_${region.regionName}`}>{region.regionName}</ListSubheader>,
            ...items
        ];
    };

    const handleOfficeChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedOffices(typeof value === 'string' ? value.split(',') : value);
    };

    const autoFillAddress = async () => {
        const fileNumber = getValues("fileNumber");
        if (!fileNumber) return;

        clearErrors("fileNumber");

        const response = await apiAdmin.get(`/get-transaction-by-file-number?fileNumber=${encodeURIComponent(fileNumber)}`,
            {
                validateStatus: (status) => status === 200 || status === 404
            }
        );

        if (response.status === 404) {
            setTransactionData(null);
            setValue("transactionType", "");
            setValue("lenderInformation", "");

            setError("fileNumber", {
                type: "manual",
                message: "File number was not found in ResWare.",
            });
            return;
        }

        const data = response.data;
        setTransactionData(data);
        setValue("transactionType", data.transactionType);

        setValue(
            "lenderInformation",
            [
                data.lender?.name ?? "Unknown",
                data.lender?.email ?? "",
                data.lender?.phone ?? "",
            ].join("\n")
        );
    };

    const partyType = watch('partyType');
    const isTimeFlexible = watch("isTimeFlexible"); 

    const populateAgentInfo = (type) => {
        if (!transactionData) return setValue('agentInformation', '');

        const agent =
            type === 'buyer' ? transactionData.sellingAgent :
                type === 'seller' ? transactionData.listingAgent :
                    null;

        if (!agent) return setValue('agentInformation', '');

        setValue('agentInformation', [
            agent?.name ?? "Unknown",
            agent?.email ?? "",
            agent?.phone ?? ""
        ].join('\n'));
    };

    useEffect(() => {
        if (partyType === 'buyer' || partyType === 'seller') {
            populateAgentInfo(partyType);
        }
    }, [partyType, transactionData]);

    useEffect(() => {
        if (submitted || pageError) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
        }
    }, [submitted, pageError]);

    useEffect(() => {
        const dt = getValues("closingDate");
        if (!dt?.isValid) return;

        if (isTimeFlexible) {
            setValue("closingDate", dt.startOf("day"), { shouldValidate: true });
        } else {
            setValue("closingDate", dt.set({ hour: 9, minute: 0, second: 0 }), { shouldValidate: true });
            setValue("timeOptions", "", { shouldDirty: true, shouldValidate: false });
        }
    }, [isTimeFlexible, getValues, setValue]);

    const onSubmit = formData => {
        setPageError(null);
        setSubmitted(false);

        if (!selectedOffices.length) {
            setPageError("Please select at least one office.");
            return;
        }

        const { closingDate: dt, ...cleaned } = formData;

        if (!dt?.isValid) {
            setPageError("Please select a valid signing date.");
            return;
        }

        cleaned.closingDate = dt.toISODate();

        if (cleaned.isTimeFlexible) {
            cleaned.closingTime = null;
            cleaned.timeOptions = (cleaned.timeOptions ?? "").trim();
        } else {
            cleaned.closingTime = dt.toFormat("HH:mm:ss");
            cleaned.timeOptions = "";
        }

        const requestObject = {
            ...cleaned,
            partyType: cleaned.partyType,
            requestedBy: adminUserProfile.displayName,
            requestedByEmail: adminUserProfile.email,
            selectedOfficeEmails: selectedOffices.join(','),
            timeZone,
            selectedAgentInformation: cleaned.agentInformation.replace(/\n/g, "<br>"),
            lenderInformation: cleaned.lenderInformation.replace(/\n/g, "<br>")
        };

        const saveSigningRequest = async () => {
            await apiAdmin.post(`/save-courtesy-signing-request`, requestObject)
                .then((response) => {
                    if (response.data) {
                        reset();
                        setSubmitted(true);
                        setSelectedOffices([]);
                        setTransactionData(null);
                    } else {
                        setPageError("There was an error saving the request. Please try again.");
                    }
                });
        };
        saveSigningRequest();
    };

    return (
        <div>
            <title>Courtesy Signing Request</title>
            <h1>Courtesy Signing Request</h1>
            {pageError && <div className="alert alert-danger" role="alert">{pageError}</div>}
            {submitted && (
                <>
                    <div className="alert alert-success alert-dismissible" role="alert">
                        Your signing request has been submitted successfully!
                    </div>
                </>
            )}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="border p-3">
                    <div className="row align-items-end mb-3">
                        <div className="col-12 col-md-6">
                            <TextField
                                label="File Number *"
                                {...register("fileNumber", { required: "This field is required" })}
                                className="w-100"
                            />
                            {errors?.fileNumber?.message && (
                                <small className="text-danger">{errors.fileNumber.message}</small>
                            )}
                        </div>

                        <div className="col-12 col-md-4">
                            <button type="button" className="btn btn-primary w-100" onClick={autoFillAddress}>Autofill Info from ResWare</button>
                        </div>
                    </div>
                    <div className="mb-3">
                        <TextField
                            label="Transaction Type *"
                            {...register("transactionType", { required: true })}
                            className="w-100"
                            InputLabelProps={{ shrink: true }}
                        />
                        {errors?.transactionType?.type === "required" && <small className="text-danger">This field is required</small>}
                    </div>
                    <div className="mb-3">
                        <TextField
                            label="Lender Information *"
                            {...register("lenderInformation", { required: true })}
                            className="w-100"
                            multiline
                            minRows={3}
                            InputLabelProps={{ shrink: true }}
                        />
                        {errors?.lenderInformation?.type === "required" && ( <small className="text-danger">This field is required</small>)}
                    </div>
                    <div className="mb-3">
                        <FormControl className="w-100" margin="normal">
                            <InputLabel id="party-type-label">Party</InputLabel>
                            <Controller
                                name="partyType"
                                control={control}
                                defaultValue=""
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        labelId="party-type-label"
                                        label="Party"
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            field.onChange(value);
                                            populateAgentInfo(value);
                                        }}
                                    >
                                        <MenuItem value="buyer">Buyer</MenuItem>
                                        <MenuItem value="seller">Seller</MenuItem>
                                    </Select>
                                )}
                            />
                            {errors?.partyType?.type === 'required' && (<small className="text-danger">Please choose Buyer or Seller</small> )}
                        </FormControl>
                    </div>
                    <div className="mb-3">
                        <Controller
                            name="agentInformation"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <TextField
                                    label="Agent Information *"
                                    className="w-100"
                                    multiline
                                    minRows={3}
                                    inputProps={{ style: { whiteSpace: 'pre-wrap' } }}
                                    InputLabelProps={{ shrink: true }}
                                    {...field}
                                />
                            )}
                        />
                        {errors?.agentInformation?.type === 'required' && (
                            <small className="text-danger">This field is required</small>
                        )}
                    </div>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" defaultChecked={false} id="isTimeFlexible" {...register("isTimeFlexible")} />
                            <label className="form-check-label" htmlFor="isTimeFlexible">Is the signing time flexible?</label>
                        </div>
                    </div>
                    {isTimeFlexible ? (
                        <div className="mb-3 row">
                            <div className="col-md-6 col-6">
                                <Controller
                                    name="closingDate"
                                    control={control}
                                    defaultValue={null}
                                    rules={{ required: "Signing date is required" }}
                                    render={({ field: { onChange, value } }) => (
                                        <DatePicker
                                            value={value}
                                            onChange={onChange}
                                            className="w-100"
                                            label="Signing Date *"
                                        />
                                    )}
                                />
                                {errors?.closingDate && (
                                    <small className="text-danger">{errors.closingDate.message}</small>
                                )}
                            </div>
                            <div className="col-md-6 col-12">
                                <TextField
                                    label="Available Time Range *"
                                    helperText="Examples: 1-4 PM, morning, or after 3 PM"
                                    {...register("timeOptions", {
                                        validate: (v) =>
                                            isTimeFlexible ? (!!v?.trim() || "Time range is required") : true
                                    })}
                                    className="w-100"
                                />
                                {errors?.timeOptions && (
                                    <small className="text-danger">{errors.timeOptions.message}</small>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="mb-3 row">
                            <div className="col-md-6 col-6">
                                <Controller
                                    name="closingDate"
                                    control={control}
                                    defaultValue={null}
                                    rules={{ required: "Signing date/time is required" }}
                                    render={({ field: { onChange, value } }) => (
                                        <DateTimePicker
                                            value={value}
                                            onChange={onChange}
                                            className="w-100"
                                            label="Signing Date/Time *"
                                        />
                                    )}
                                />
                                {errors?.closingDate && (
                                    <small className="text-danger">{errors.closingDate.message}</small>
                                )}
                            </div>
                            <div className="col-md-6 col-12">
                                <ToggleButtonGroup
                                    value={timeZone}
                                    exclusive
                                    onChange={(e, newTimeZone) => { if (newTimeZone) setTimeZone(newTimeZone); }}
                                    aria-label="Time Zone"
                                >
                                        <ToggleButton value="Mountain Time" aria-label="Mountain Time">Mountain Time</ToggleButton>
                                        <ToggleButton value="Pacific Time" aria-label="Pacific Time">Pacific Time</ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                        </div>
                    )}
                    <div className="col-12 mb-3">
                        <TextField
                            label="Special Circumstances"
                            helperText="Examples: deceased, divorced"
                            {...register("specialCircumstances")}
                            className="w-100"
                        />
                    </div>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" defaultChecked={false} id="isSpanishSpeaking" {...register("isSpanishSpeaking")} />
                            <label className="form-check-label" htmlFor="isSpanishSpeaking">Spanish Speaking?</label>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" defaultChecked={false} id="hasFirstAndSecondLoan" {...register("hasFirstAndSecondLoan")} />
                            <label className="form-check-label" htmlFor="hasFirstAndSecondLoan">Is this a 1st and a 2nd?</label>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" defaultChecked={false} id="hasPowerOfAttorney" {...register("hasPowerOfAttorney")} />
                            <label className="form-check-label" htmlFor="hasPowerOfAttorney">POA?</label>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" defaultChecked={false} id="isSameDaySign" {...register("isSameDaySign")} />
                            <label className="form-check-label" htmlFor="isSameDaySign">Is this same day sign/fund?</label>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-8">
                                <FormControl fullWidth error={!!errors.offices}>
                                    <InputLabel id="multiple-office-label">Office(s) *</InputLabel>
                                    <Controller
                                        name="offices"
                                        control={control}
                                        defaultValue={selectedOffices}
                                        rules={{ required: 'At least one office is required' }}
                                        render={({ field }) => (
                                            <Select
                                                labelId="multiple-office-label"
                                                id="multiple-office"
                                                multiple
                                                value={selectedOffices.length ? selectedOffices : []}
                                                onChange={(event) => { field.onChange(event.target.value); handleOfficeChange(event); }}
                                                input={<OutlinedInput label="Office(s)" />}
                                            >
                                                {regions.filter(r => r.offices && r.offices.length > 0).map((region) => renderSelectGroup(region))}
                                            </Select>
                                        )}
                                    />
                                    {errors.offices && <small className="text-danger">{errors.offices.message}</small>}
                                    <FormHelperText>
                                        You can select more than one office if they're flexible.
                                    </FormHelperText>

                                </FormControl>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 mb-3">
                        <TextField
                            label="Escrow CC Email"
                            {...register("escrowCCEmails")}
                            className="w-100"
                            helperText="The requester will be CC'd on all email replies. If you would like anyone else to see the email replies (team emails, EOs) add their emails to this section separated by commas."
                        />
                    </div>
                    <div className="mb-3">
                        <input type="submit" value="Submit Request" className="btn btn-primary" aria-label="Submit Request" />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CourtesySigningRequest;
